// Global Instructions Rule Applied!

import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { fetchAssetTransactionHeaderOptions } from '../../asset_transactions/services/assetTransactionsService.ts'
import { SelectListEntry } from '../../../state_manager/data_models/select_list_entry.ts'
import { useCurrencies } from '../../currencies/useCurrencies.ts'
import { PIPELINE_TYPE_FUNDRAISING } from '../../../state_manager/globals.ts'
import { fundStatusOptions } from '../../global_configs/fundStatusDisplay.tsx'
import { FundTermsFieldOptions } from '../utils/fundTermsFieldConfigs.ts'

/**
 * useFundTermsFieldOptions — the select lists behind every fund field config
 * (Terms tab sections + the New Fund form's Details section), read from
 * globalConfigs plus one accounts fetch for the Fund Manager picker.
 *
 * `fundId` is only used to resolve the currency symbol of an EXISTING fund
 * for the currency-typed fields; pass null on the create form.
 */
export function useFundTermsFieldOptions(fundId: string | null) {
  const { getCurrencyById, getCurrencyOptions } = useCurrencies()

  const timeUnits = useSelector((state: any) => state.globalConfigs?.time_units || [])
  const referenceDateTypes = useSelector((state: any) => state.globalConfigs?.reference_date_types || [])
  const gpCommitmentBases = useSelector((state: any) => state.globalConfigs?.gp_commitment_bases || [])
  const feeBases = useSelector((state: any) => state.globalConfigs?.fee_bases || [])
  const compoundingFrequencies = useSelector((state: any) => state.globalConfigs?.compounding_frequencies || [])
  const dayCountConventions = useSelector((state: any) => state.globalConfigs?.day_count_conventions || [])
  const lateCloserPolicies = useSelector((state: any) => state.globalConfigs?.late_closer_policies || [])
  const feeBreakTreatments = useSelector((state: any) => state.globalConfigs?.fee_break_treatments || [])
  const waivedFeeSettlements = useSelector((state: any) => state.globalConfigs?.waived_fee_settlements || [])
  const feePeriodSwitchTimings = useSelector((state: any) => state.globalConfigs?.fee_period_switch_timings || [])
  const funds = useSelector((state: any) => state.globalConfigs?.funds || [])
  const pipelines = useSelector((state: any) => state.globalConfigs?.pipelines || [])
  const fundStatuses = useSelector((state: any) => state.globalConfigs?.fund_statuses ?? [])

  // Accounts for the Fund Manager select (same source as the co-investor
  // account pickers)
  const [accountOptions, setAccountOptions] = useState<SelectListEntry[]>([])
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const token = getAccessToken(store.getState().auth)
      const result = await fetchAssetTransactionHeaderOptions(token)
      if (!cancelled && result.data) {
        setAccountOptions(
          (result.data.companies || []).map((c: any) => ({ value: c.value, label: c.label || '' }))
        )
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const currencySymbol = useMemo(() => {
    const fund = funds.find((f: any) => f.id === fundId)
    if (!fund?.functional_currency_id) return undefined
    return getCurrencyById(fund.functional_currency_id)?.symbol ?? undefined
  }, [funds, fundId, getCurrencyById])

  const currencyOptions = useMemo(
    () => getCurrencyOptions().map((c) => ({ value: c.value, label: c.label })),
    [getCurrencyOptions]
  )

  const fieldOptions: FundTermsFieldOptions = useMemo(
    () => ({
      accountOptions,
      timeUnitOptions: timeUnits.map((u: any) => ({ value: u.id, label: u.label })),
      anchorOptions: referenceDateTypes.map((r: any) => ({ value: r.id, label: r.label })),
      gpCommitmentBasisOptions: gpCommitmentBases.map((b: any) => ({ value: b.id, label: b.label })),
      feeBasisOptions: feeBases.map((b: any) => ({ value: b.id, label: b.label })),
      frequencyOptions: compoundingFrequencies.map((f: any) => ({ value: f.id, label: f.label })),
      dayCountOptions: dayCountConventions.map((d: any) => ({ value: d.id, label: d.label })),
      lateCloserPolicyOptions: lateCloserPolicies.map((p: any) => ({ value: p.id, label: p.label })),
      feeBreakTreatmentOptions: feeBreakTreatments.map((t: any) => ({ value: t.id, label: t.label })),
      feeBreakTreatmentHelp: feeBreakTreatments.map((t: any) => ({ label: t.label, description: t.description })),
      waivedFeeSettlementOptions: waivedFeeSettlements.map((s: any) => ({ value: s.id, label: s.label })),
      waivedFeeSettlementHelp: waivedFeeSettlements.map((s: any) => ({ label: s.label, description: s.description })),
      feePeriodSwitchTimingOptions: feePeriodSwitchTimings.map((t: any) => ({ value: t.id, label: t.label })),
      pipelineOptions: pipelines
        .filter((p: any) => p && p.type === PIPELINE_TYPE_FUNDRAISING)
        .map((p: any) => ({ value: p.id, label: p.name || '' })),
      fundraisingStatusOptions: fundStatusOptions(fundStatuses, 'fundraising'),
      investmentStatusOptions: fundStatusOptions(fundStatuses, 'investment'),
      currencyOptions,
      currencySymbol
    }),
    [
      accountOptions,
      timeUnits,
      referenceDateTypes,
      gpCommitmentBases,
      feeBases,
      compoundingFrequencies,
      dayCountConventions,
      lateCloserPolicies,
      feeBreakTreatments,
      waivedFeeSettlements,
      feePeriodSwitchTimings,
      pipelines,
      fundStatuses,
      currencyOptions,
      currencySymbol
    ]
  )

  return { fieldOptions, fundStatuses }
}
